/* La fiche d'une usine : son identité sur les documents, ses contacts, et tout
   ce qui lui est rattaché — produits, dossiers, clients, ventes, commissions. */

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAccesUsine, useClients, useCommissions, useContactsUsine, useDocumentsUsine, usePackaging,
  useProduits, useUsine, useVentes,
} from "@/lib/donnees";
import { autorise, useNiveau, useSession } from "@/lib/session";
import { supabase } from "@/lib/supabase";
import { Empty, Kpi, Kpis, Logo, Onglets, Section, Tag } from "@/ui/primitives";
import { Identite } from "@/ui/Fiche";
import { Champ } from "@/ui/Drawer";
import { Icon } from "@/ui/Icon";
import { Table, dansUnChamp } from "@/ui/Table";
import { useToast } from "@/ui/Toast";
import { dateFr, entier, euro, euroCourt } from "@/lib/format";
import { GROUPES_USINE } from "@/domain/fiches";
import { Journal } from "@/features/journal/Journal";
import { enregistrerFiche } from "@/features/journal/actions";
import type { Usine } from "@/types/metier";
import { ETAPES_PACKAGING, etatDe } from "@/domain/dossiers";
import { EditeurContacts, Responsables } from "@/features/contacts/EditeurContacts";
import { DocumentsUsine } from "./Documents";
import { AccesUsine } from "./Acces";
import { etapeDe } from "@/domain/ventes";

type Onglet =
  | "fiche" | "documents" | "produits" | "packaging" | "clients" | "ventes" | "acces" | "suivi";

export function FicheUsine() {
  const { id } = useParams<{ id: string }>();
  const navigue = useNavigate();
  const toast = useToast();
  const niveau = useNiveau();
  const { profil } = useSession();
  const modifiable = autorise(niveau, "agent");

  const { donnees: usine, chargement, erreur, recharger } = useUsine(id);
  const { donnees: produits } = useProduits();
  const { donnees: ventes } = useVentes();
  const { donnees: dossiers } = usePackaging();
  const { donnees: commissions } = useCommissions();
  const { donnees: clients } = useClients();
  const { donnees: contacts, recharger: rechargerContacts } = useContactsUsine(id);
  const { donnees: documents, chargement: chargeDocs, recharger: rechargerDocs } =
    useDocumentsUsine(id);
  const { donnees: acces, recharger: rechargerAcces } = useAccesUsine(id);
  const [onglet, setOnglet] = useState<Onglet>("fiche");

  const nomClient = useMemo(() => new Map(clients.map((c) => [c.id, c.nom] as const)), [clients]);

  const siens = useMemo(() => produits.filter((p) => p.usine_id === id), [produits, id]);
  const sesVentes = useMemo(() => ventes.filter((v) => v.usine_id === id), [ventes, id]);
  const sesDossiers = useMemo(() => dossiers.filter((d) => d.usine_id === id), [dossiers, id]);
  const sesComm = useMemo(() => commissions.filter((c) => c.usine_id === id), [commissions, id]);

  /* Ses clients : ceux qui lui ont acheté, avec le cumul et la dernière vente. */
  const sesClients = useMemo(() => {
    const m = new Map<string, { id: string; nom: string; nb: number; montant: number; derniere: string }>();
    for (const v of sesVentes) {
      const cid = v.client_id ?? "—";
      const e = m.get(cid) ?? {
        id: cid, nom: v.client_id ? (nomClient.get(v.client_id) ?? "—") : "—",
        nb: 0, montant: 0, derniere: "",
      };
      e.nb += 1;
      e.montant = Math.round((e.montant + Number(v.montant)) * 100) / 100;
      if (v.date_document > e.derniere) e.derniere = v.date_document;
      m.set(cid, e);
    }
    return [...m.values()].sort((a, b) => b.montant - a.montant);
  }, [sesVentes, nomClient]);

  async function majUsine(patch: Record<string, unknown>, message: string) {
    if (!usine) return;
    const { error } = await supabase.from("usines").update(patch).eq("id", usine.id);
    if (error) {
      toast(error.message);
      return;
    }
    recharger();
    toast(message);
  }

  /* Enregistrer une fiche usine, c'est aussi inscrire ce qui a changé : les
     prix et les délais d'une usine se discutent, et on veut savoir quand ils
     ont bougé — et par qui. */
  const enregistrer = async (champs: Partial<Usine>) => {
    if (!usine) throw new Error("Usine inconnue.");
    if (!profil) throw new Error("Profil inconnu : reconnectez-vous.");
    await enregistrerFiche<Usine>(
      { sujet: "usines", sujetId: usine.id, auteur: profil.id },
      "usines",
      usine as unknown as Record<string, unknown>,
      champs as Record<string, unknown>,
    );
    recharger();
    toast("Fiche enregistrée.");
  };

  if (!modifiable) {
    return (
      <div className="pad">
        <Empty titre="Réservé à l'équipe Omnix" detail="Cette fiche n'est pas ouverte depuis un compte usine." />
      </div>
    );
  }
  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;
  if (chargement) return <div className="pad"><div className="chargement">Chargement…</div></div>;
  if (!usine) {
    return (
      <div className="pad">
        <Empty titre="Usine introuvable" detail="Choisissez une usine dans la liste." />
      </div>
    );
  }

  const onglets: { cle: Onglet; nom: string; n?: number }[] = [
    { cle: "fiche", nom: "Fiche" },
    { cle: "documents", nom: "Documents", n: documents.length },
    { cle: "produits", nom: "Produits", n: siens.length },
    { cle: "packaging", nom: "Packaging", n: sesDossiers.length },
    { cle: "clients", nom: "Clients", n: sesClients.length },
    { cle: "ventes", nom: "Ventes", n: sesVentes.length },
    ...(niveau === "admin"
      ? [{ cle: "acces" as const, nom: "Accès", n: acces.length }]
      : []),
    { cle: "suivi", nom: "Suivi" },
  ];

  return (
    <div className="pad">
      <div className="fiche-tete">
        <Logo src={usine.logo_url} nom={usine.nom} couleur={usine.couleur} taille={44} />
        <div className="fiche-t">
          <div className="section-t">{usine.nom}</div>
          <div className="section-n">
            {[usine.ville, usine.pays, usine.categorie].filter(Boolean).join(" · ")} ·{" "}
            {entier(siens.length)} références
          </div>
        </div>
        <div className="fiche-a fiche-a-centre">
          {usine.actif ? <Tag ton="ok" dot>active</Tag> : <Tag dot>en attente</Tag>}
          <button type="button" className="btn" onClick={() => navigue("/usines")}>
            <Icon n="retour" cls="" />
            Toutes les usines
          </button>
        </div>
      </div>

      <div className="fiche-parcours">
        <Onglets items={onglets} actif={onglet} onChange={setOnglet} />
      </div>

      {onglet === "fiche" ? (
        <>
          <Kpis>
            <Kpi k="Références" v={entier(siens.length)} n={`${siens.filter((p) => p.actif).length} au catalogue`} />
            <Kpi
              k="Chiffre d'affaires suivi"
              v={euroCourt(sesVentes.reduce((s, v) => s + Number(v.montant), 0))}
              n={`${sesVentes.length} documents`}
            />
            <Kpi
              k="Commissions"
              v={euroCourt(sesComm.reduce((s, c) => s + Number(c.montant), 0))}
              n={`${sesComm.length} lignes`}
            />
            <Kpi k="Clients servis" v={entier(sesClients.length)} n={`${documents.length} documents joints`} />
          </Kpis>

          <Identite
            valeur={usine}
            groupes={GROUPES_USINE}
            modifiable={modifiable}
            enregistrer={enregistrer}
          />

          <div className="grid2">
            <div>
              <Section titre="Sur les documents" note="ce qui signe ses offres et ses factures">
                <div className="card card-pad">
                  <Champ id="u-couleur" label="Couleur des documents">
                    <span className="couleur-champ">
                      <input
                        id="u-couleur"
                        type="color"
                        defaultValue={usine.couleur}
                        onBlur={(e) => {
                          if (e.target.value === usine.couleur) return;
                          void majUsine({ couleur: e.target.value }, "Couleur enregistrée.");
                        }}
                      />
                      <span className="mono">{usine.couleur}</span>
                    </span>
                  </Champ>
                  <dl className="kv kv-haut">
                    <dt>Logo</dt>
                    <dd>{usine.logo_url ? "fourni" : "initiales"}</dd>
                    <dt>Chiffre d'affaires</dt>
                    <dd className="mono">
                      {euro(sesVentes.reduce((s, v) => s + Number(v.montant), 0))}
                    </dd>
                    <dt>Commissions</dt>
                    <dd className="mono">
                      {euro(sesComm.reduce((s, c) => s + Number(c.montant), 0))}
                    </dd>
                  </dl>
                </div>
              </Section>
              <Section titre="Responsables" note="qui reçoit quoi">
                <div className="card card-pad">
                  <Responsables liste={contacts} />
                </div>
              </Section>
            </div>
            <div>
              <EditeurContacts
                table="contacts_usine"
                colonne="usine_id"
                parent={usine.id}
                liste={contacts}
                modifiable={modifiable}
                recharger={rechargerContacts}
              />
            </div>
          </div>
        </>
      ) : null}

      {onglet === "documents" ? (
        <DocumentsUsine
          usineId={id ?? ""}
          documents={documents}
          produits={siens}
          chargement={chargeDocs}
          onChange={rechargerDocs}
        />
      ) : null}

      {onglet === "produits" ? (
        <Section titre="Produits" note={`${siens.length} références`}>
          <Table
            lignes={siens}
            cle={(p) => p.id}
            chercher="Référence ou désignation"
            colonnes={[
              { cle: "ref", titre: "Référence", type: "ref", filtre: "texte", sous: (p) => p.nom },
              { cle: "nom", titre: "Désignation", classe: "name", mobile: false, filtre: "texte" },
              { cle: "gamme", titre: "Gamme", filtre: "select", mobile: false },
              { cle: "conditionnement", titre: "Cond.", type: "ref", mobile: false },
              { cle: "prix", titre: "Prix de vente", court: "Prix", type: "euro", total: true,
                agregat: "moyenne", totalTitre: "Prix moyen", nombre: (p) => Number(p.prix ?? 0) },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "packaging" ? (
        <Section titre="Dossiers de packaging" note={`${sesDossiers.length} dossiers`}>
          <Table
            lignes={sesDossiers}
            cle={(d) => d.id}
            colonnes={[
              { cle: "reference", titre: "Dossier", type: "ref", filtre: "texte",
                sous: (d) => d.intitule },
              { cle: "intitule", titre: "Intitulé", classe: "name", mobile: false },
              { cle: "client_id", titre: "Client", filtre: "select", mobile: false,
                texte: (d) => (d.client_id ? (nomClient.get(d.client_id) ?? "—") : "—") },
              {
                cle: "etape", titre: "Étape", filtre: "select",
                texte: (d) => etatDe(ETAPES_PACKAGING, d.etape).nom,
                rendu: (d) => {
                  const e = etatDe(ETAPES_PACKAGING, d.etape);
                  return <Tag ton={e.ton} dot>{e.nom}</Tag>;
                },
              },
              { cle: "echeance", titre: "Échéance", type: "date", mobile: false },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "clients" ? (
        <Section titre="Clients servis" note="cumul des ventes passées par cette usine">
          <Table
            lignes={sesClients}
            cle={(c) => c.id}
            colonnes={[
              { cle: "nom", titre: "Client", filtre: "texte",
                sous: (c) => `${c.nb} ${c.nb > 1 ? "ventes" : "vente"}` },
              { cle: "nb", titre: "Ventes", type: "num", total: true, mobile: false },
              { cle: "montant", titre: "Chiffre d'affaires", court: "CA", type: "euro", total: true },
              { cle: "derniere", titre: "Dernière vente", court: "Dernière", type: "date",
                mobile: false },
            ]}
          />
        </Section>
      ) : null}

      {onglet === "ventes" ? (
        <Section titre="Ventes" note={`${sesVentes.length} documents`}>
          <Table
            lignes={sesVentes}
            cle={(v) => v.id}
            chercher="Document ou client"
            onClic={(v, ev) => {
              if (!dansUnChamp(ev)) navigue(`/ventes/${v.id}`);
            }}
            colonnes={[
              { cle: "reference", titre: "Document", type: "ref", filtre: "texte",
                sous: (v) => (v.client_id ? (nomClient.get(v.client_id) ?? "—") : "—") },
              { cle: "client_id", titre: "Client", filtre: "select", mobile: false,
                texte: (v) => (v.client_id ? (nomClient.get(v.client_id) ?? "—") : "—") },
              { cle: "montant", titre: "Montant HT", court: "Montant", type: "euro", total: true,
                nombre: (v) => Number(v.montant) },
              { cle: "etape", titre: "Étape", filtre: "select",
                texte: (v) => etapeDe(v.etape).nom,
                rendu: (v) => <Tag ton={etapeDe(v.etape).ton} dot>{etapeDe(v.etape).court}</Tag> },
              { cle: "date_document", titre: "Date", type: "date", mobile: false,
                rendu: (v) => <span className="mono">{dateFr(v.date_document)}</span> },
            ]}
          />
        </Section>
      ) : null}
      {onglet === "acces" && niveau === "admin" ? (
        <AccesUsine
          usineId={usine.id}
          nomUsine={usine.nom}
          comptes={acces}
          onChange={rechargerAcces}
        />
      ) : null}

      {onglet === "suivi" ? <Journal sujet="usines" sujetId={usine.id} /> : null}
    </div>
  );
}
